/**
 * Hora Advanced Agenda — public List View enhancement.
 *
 * Vanilla TS. PHP ships fully-rendered HTML with time-grouped rows; this
 * script handles:
 *  - Outer tab switching (agendas within the group).
 *  - Inner tab switching (panels within the active agenda).
 *  - Section card → details drawer (via shared section-drawer-bridge).
 *  - Add-to-calendar `<a>` links keep their default behavior (open in a new
 *    tab); only clicks outside those links trigger the drawer.
 */
import { openSectionDrawer, pulseCard } from "../shared/section-drawer-bridge";
import { initAddToCalendarTracking } from "../shared/analytics";
import { startLiveState, formatTimeRange, } from "../shared/live-state";
import { mountNowLine, repositionListNowLine } from "../shared/now-line";
import { applySectionStates, mountSectionStates, } from "../shared/section-state";
const CONTAINER_SELECTOR = ".hora-agenda-list[data-group-id]";
const OUTER_TAB_SELECTOR = '.hora-agenda-list__tab[role="tab"]';
const OUTER_PANEL_SELECTOR = '.hora-agenda-list__agenda[role="tabpanel"]';
const INNER_TAB_SELECTOR = '.hora-agenda-list__panel-tab[role="tab"]';
const INNER_PANEL_SELECTOR = '.hora-agenda-list__panel[role="tabpanel"]';
const CARD_SELECTOR = "[data-section-id]";
const PULSE_CLASS = "hora-agenda-list__section--pulse";
const LINE_WIDTH_VAR = "--hora-list-line-width";
const URL_PARAM = "agenda";
const URL_PARAM_PANEL = "panel";
const URL_PARAM_SECTION = "section";
function collect(container) {
    return {
        container,
        outerTabs: Array.from(container.querySelectorAll(OUTER_TAB_SELECTOR)),
        outerPanels: Array.from(container.querySelectorAll(OUTER_PANEL_SELECTOR)),
    };
}
/* ---------- URL + storage keys --------------------------------------- */
// Outer (agenda/day) selection lives in the URL so it survives a layout
// switch — both [hora_advanced_agenda] layouts read the same `?agenda=`
// param. Inner (panel) selection stays in sessionStorage; panels are
// scoped within one agenda and don't cross layouts.
function syncUrl(agendaId) {
    try {
        const url = new URL(window.location.href);
        if (url.searchParams.get(URL_PARAM) === agendaId)
            return;
        url.searchParams.set(URL_PARAM, agendaId);
        window.history.replaceState({}, "", url.toString());
    }
    catch (_a) {
        /* History API unavailable — selection still works in-page */
    }
}
function readUrlAgenda() {
    try {
        return new URL(window.location.href).searchParams.get(URL_PARAM);
    }
    catch (_a) {
        return null;
    }
}
function syncPanelUrl(panelId) {
    try {
        const url = new URL(window.location.href);
        if (url.searchParams.get(URL_PARAM_PANEL) === panelId)
            return;
        url.searchParams.set(URL_PARAM_PANEL, panelId);
        window.history.replaceState({}, "", url.toString());
    }
    catch (_a) {
        /* History API unavailable — selection still works in-page */
    }
}
function readUrlPanel() {
    try {
        return new URL(window.location.href).searchParams.get(URL_PARAM_PANEL);
    }
    catch (_a) {
        return null;
    }
}
function readUrlSection() {
    try {
        return new URL(window.location.href).searchParams.get(URL_PARAM_SECTION);
    }
    catch (_a) {
        return null;
    }
}
function clearUrlParam(name) {
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(name))
            return;
        url.searchParams.delete(name);
        window.history.replaceState({}, "", url.toString());
    }
    catch (_a) {
        /* History API unavailable */
    }
}
function cssEscape(value) {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(value);
    }
    return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
function innerKey(container, agendaId) {
    const gid = container.getAttribute("data-group-id");
    return gid ? `hora-agenda-list:active-panel:${gid}:${agendaId}` : null;
}
function read(key) {
    if (!key)
        return null;
    try {
        return sessionStorage.getItem(key);
    }
    catch (_a) {
        return null;
    }
}
function write(key, value) {
    if (!key)
        return;
    try {
        sessionStorage.setItem(key, value);
    }
    catch (_a) {
        /* storage disabled — non-fatal */
    }
}
/* ---------- Outer tabs (agendas) ------------------------------------- */
function activateOuter(state, target, focus) {
    const agendaId = target.getAttribute("data-agenda-id");
    if (!agendaId)
        return;
    for (const tab of state.outerTabs) {
        const isActive = tab === target;
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.tabIndex = isActive ? 0 : -1;
    }
    for (const panel of state.outerPanels) {
        const match = panel.getAttribute("data-agenda-id") === agendaId;
        if (match) {
            panel.removeAttribute("hidden");
        }
        else {
            panel.setAttribute("hidden", "");
        }
    }
    state.container.setAttribute("data-active-agenda", agendaId);
    syncUrl(agendaId);
    if (focus)
        target.focus();
    state.container.dispatchEvent(new CustomEvent("hora:agenda-list:tab-change", {
        detail: { agendaId: Number(agendaId) },
        bubbles: true,
        cancelable: true,
    }));
    // Restore inner-tab selection for this agenda, if saved.
    const activePanel = state.outerPanels.find((p) => p.getAttribute("data-agenda-id") === agendaId);
    if (activePanel) {
        restoreInner(state, activePanel, agendaId);
    }
}
/* ---------- Inner tabs (panels) -------------------------------------- */
function activateInner(state, outerPanel, target, focus) {
    const panelId = target.getAttribute("data-panel-id");
    if (!panelId)
        return;
    const innerTabs = outerPanel.querySelectorAll(INNER_TAB_SELECTOR);
    const innerPanels = outerPanel.querySelectorAll(INNER_PANEL_SELECTOR);
    innerTabs.forEach((tab) => {
        const isActive = tab === target;
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.tabIndex = isActive ? 0 : -1;
    });
    innerPanels.forEach((panel) => {
        const match = panel.getAttribute("data-panel-id") === panelId;
        if (match) {
            panel.removeAttribute("hidden");
        }
        else {
            panel.setAttribute("hidden", "");
        }
    });
    const agendaId = outerPanel.getAttribute("data-agenda-id") || "";
    write(innerKey(state.container, agendaId), panelId);
    syncPanelUrl(panelId);
    if (focus)
        target.focus();
    state.container.dispatchEvent(new CustomEvent("hora:agenda-list:panel-tab-change", {
        detail: { agendaId: Number(agendaId), panelId: Number(panelId) },
        bubbles: true,
        cancelable: true,
    }));
}
function restoreInner(state, outerPanel, agendaId) {
    const saved = read(innerKey(state.container, agendaId));
    if (!saved)
        return;
    const innerTabs = outerPanel.querySelectorAll(INNER_TAB_SELECTOR);
    const match = Array.from(innerTabs).find((t) => t.getAttribute("data-panel-id") === saved);
    if (match) {
        activateInner(state, outerPanel, match, false);
    }
}
/* ---------- Click + keyboard ----------------------------------------- */
function onClick(state, evt) {
    const target = evt.target;
    if (!target)
        return;
    // Outer tab?
    const outerTab = target.closest(OUTER_TAB_SELECTOR);
    if (outerTab && state.outerTabs.indexOf(outerTab) !== -1) {
        evt.preventDefault();
        activateOuter(state, outerTab, false);
        return;
    }
    // Inner tab? (live lookup — DOM may have changed)
    const innerTab = target.closest(INNER_TAB_SELECTOR);
    if (innerTab && state.container.contains(innerTab)) {
        const outerPanel = innerTab.closest(OUTER_PANEL_SELECTOR);
        if (outerPanel) {
            evt.preventDefault();
            activateInner(state, outerPanel, innerTab, false);
            return;
        }
    }
    // Section card → drawer. Respect real anchor clicks (add-to-calendar).
    const card = target.closest(CARD_SELECTOR);
    if (card && state.container.contains(card)) {
        if (target.closest("a[href]"))
            return;
        const sectionId = card.getAttribute("data-section-id");
        if (sectionId) {
            evt.preventDefault();
            openSectionDrawer(state.container, sectionId, card, PULSE_CLASS, "list");
        }
    }
}
function onKeydown(state, evt) {
    const target = evt.target;
    if (!target)
        return;
    // Outer tab navigation
    const outerTab = target.closest(OUTER_TAB_SELECTOR);
    if (outerTab && state.outerTabs.indexOf(outerTab) !== -1) {
        const next = pickNeighbor(state.outerTabs, outerTab, evt.key);
        if (next) {
            evt.preventDefault();
            activateOuter(state, next, true);
        }
        return;
    }
    // Inner tab navigation
    const innerTab = target.closest(INNER_TAB_SELECTOR);
    if (innerTab && state.container.contains(innerTab)) {
        const outerPanel = innerTab.closest(OUTER_PANEL_SELECTOR);
        if (!outerPanel)
            return;
        const innerTabs = Array.from(outerPanel.querySelectorAll(INNER_TAB_SELECTOR));
        const next = pickNeighbor(innerTabs, innerTab, evt.key);
        if (next) {
            evt.preventDefault();
            activateInner(state, outerPanel, next, true);
        }
        return;
    }
    // Card → drawer (Enter/Space)
    const card = target.closest(CARD_SELECTOR);
    if (card && state.container.contains(card) && (evt.key === "Enter" || evt.key === " ")) {
        const sectionId = card.getAttribute("data-section-id");
        if (sectionId) {
            evt.preventDefault();
            openSectionDrawer(state.container, sectionId, card, PULSE_CLASS, "list");
        }
    }
}
function pickNeighbor(tabs, current, key) {
    if (tabs.length === 0)
        return null;
    const idx = tabs.indexOf(current);
    if (idx === -1)
        return null;
    switch (key) {
        case "ArrowRight":
            return tabs[(idx + 1) % tabs.length];
        case "ArrowLeft":
            return tabs[(idx - 1 + tabs.length) % tabs.length];
        case "Home":
            return tabs[0];
        case "End":
            return tabs[tabs.length - 1];
        case "Enter":
        case " ":
            return current;
        default:
            return null;
    }
}
/* ---------- Line-width tracking -------------------------------------- */
/**
 * Write the container's current layout width into `--hora-list-line-width`
 * as an inline custom property. Cards derive their own width from this var,
 * so every card always equals the container's width (desktop) or
 * `mobile-ratio × container-width` (mobile).
 *
 * ResizeObserver drives re-measurement. Works when the container is
 * initially `hidden` (inside a collapsed tabpanel) — the observer fires as
 * soon as it gains layout.
 */
function updateLineWidth(container) {
    // `clientWidth` is layout-box-inclusive of padding; we want the content
    // width the flex row can consume. Fall back to `getBoundingClientRect` if
    // the element has no layout yet (returns 0 on hidden elements pre-layout).
    const rect = container.getBoundingClientRect();
    const width = Math.max(0, Math.floor(rect.width));
    if (width <= 0)
        return;
    container.style.setProperty(LINE_WIDTH_VAR, `${width}px`);
}
function observeLineWidth(container) {
    updateLineWidth(container);
    if (typeof ResizeObserver === "undefined") {
        // Old browser fallback — listen to window resize.
        window.addEventListener("resize", () => updateLineWidth(container), { passive: true });
        return;
    }
    const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
            if (entry.target === container) {
                updateLineWidth(container);
            }
        }
    });
    observer.observe(container);
}
/* ---------- Restore + bootstrap -------------------------------------- */
function restoreOuter(state) {
    const requested = readUrlAgenda();
    if (requested) {
        const match = state.outerTabs.find((t) => t.getAttribute("data-agenda-id") === requested);
        if (match) {
            activateOuter(state, match, false);
            restoreInnerFromUrl(state);
            return;
        }
        // URL param doesn't match a real tab — clean it up to the server's choice.
        const fallback = state.container.getAttribute("data-active-agenda");
        if (fallback)
            syncUrl(fallback);
    }
    // No URL param (or it was stale): keep the server-rendered active outer
    // tab and restore the inner-panel selection that was sitting under it.
    const active = state.outerPanels.find((p) => !p.hasAttribute("hidden"));
    const agendaId = (active === null || active === void 0 ? void 0 : active.getAttribute("data-agenda-id")) || "";
    if (active && agendaId)
        restoreInner(state, active, agendaId);
    restoreInnerFromUrl(state);
}
// URL `?panel=` overrides sessionStorage on first load. Only applied to the
// currently-active agenda — switching days later falls back to per-agenda
// sessionStorage so a stale URL panel doesn't follow you across days.
function restoreInnerFromUrl(state) {
    const panelId = readUrlPanel();
    if (!panelId)
        return;
    const active = state.outerPanels.find((p) => !p.hasAttribute("hidden"));
    if (!active)
        return;
    const tab = active.querySelector(`${INNER_TAB_SELECTOR}[data-panel-id="${cssEscape(panelId)}"]`);
    if (tab)
        activateInner(state, active, tab, false);
}
// Deep-link: `?section=<id>` pre-opens the section drawer. If the section
// lives on a different agenda day or inner panel than what's currently
// active, switch both tabs first so closing the drawer reveals the card.
// One-shot: the param is stripped from the URL immediately.
function openDrawerFromUrl(state) {
    const sectionId = readUrlSection();
    if (!sectionId)
        return;
    clearUrlParam(URL_PARAM_SECTION);
    const card = state.container.querySelector(`[data-section-id="${cssEscape(sectionId)}"]`);
    if (!card)
        return;
    const targetOuter = card.closest(OUTER_PANEL_SELECTOR);
    const targetAgendaId = (targetOuter === null || targetOuter === void 0 ? void 0 : targetOuter.getAttribute("data-agenda-id")) || "";
    if (targetAgendaId && state.container.getAttribute("data-active-agenda") !== targetAgendaId) {
        const outerTab = state.outerTabs.find((t) => t.getAttribute("data-agenda-id") === targetAgendaId);
        if (outerTab)
            activateOuter(state, outerTab, false);
    }
    const targetInner = card.closest(INNER_PANEL_SELECTOR);
    const targetPanelId = (targetInner === null || targetInner === void 0 ? void 0 : targetInner.getAttribute("data-panel-id")) || "";
    if (targetOuter && targetPanelId) {
        const currentInner = targetOuter.querySelector(`${INNER_PANEL_SELECTOR}:not([hidden])`);
        if ((currentInner === null || currentInner === void 0 ? void 0 : currentInner.getAttribute("data-panel-id")) !== targetPanelId) {
            const innerTab = targetOuter.querySelector(`${INNER_TAB_SELECTOR}[data-panel-id="${cssEscape(targetPanelId)}"]`);
            if (innerTab)
                activateInner(state, targetOuter, innerTab, false);
        }
    }
    void openSectionDrawer(state.container, sectionId, null, PULSE_CLASS, "list");
}
/* ---------- Live state + now-line ------------------------------------ */
const AGENDA_SELECTOR = ".hora-agenda-list__agenda[data-agenda-id]";
function applyListDelays(agenda, payload, prevDelays) {
    for (const row of payload.delays) {
        const card = agenda.querySelector(`.hora-agenda-list__section[data-section-id="${row.section_id}"]`);
        if (!card)
            continue;
        const baseStart = Number(card.getAttribute("data-start-min") || "NaN");
        const baseEnd = Number(card.getAttribute("data-end-min") || "NaN");
        if (!Number.isFinite(baseStart))
            continue;
        const eff = row.effective_delay_minutes | 0;
        const newStart = baseStart + eff;
        const newEnd = Number.isFinite(baseEnd) ? baseEnd + eff : null;
        card.setAttribute("data-delay-min", String(row.delay_minutes | 0));
        card.setAttribute("data-effective-start-min", String(newStart));
        if (newEnd !== null) {
            card.setAttribute("data-effective-end-min", String(newEnd));
        }
        const wrapper = card.querySelector(".hora-agenda-list__section-time-text");
        const timeEl = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector("time");
        if (wrapper && timeEl) {
            const fmt = wrapper.getAttribute("data-time-format") || "H:i";
            timeEl.textContent = formatTimeRange(newStart, newEnd, fmt);
            timeEl.dateTime = formatIso(newStart);
        }
        const prev = prevDelays.get(row.section_id);
        if (prev !== undefined && prev !== eff) {
            pulseCard(agenda, String(row.section_id), "hora-agenda-list__section--pulse");
        }
        prevDelays.set(row.section_id, eff);
    }
}
function formatIso(min) {
    const total = ((min % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
}
function initLiveForAgenda(agenda) {
    const agendaId = Number(agenda.getAttribute("data-agenda-id") || "0");
    const tz = agenda.getAttribute("data-tz") || "UTC";
    const agendaDate = agenda.getAttribute("data-agenda-date") || "";
    const serverNow = agenda.getAttribute("data-server-now");
    mountNowLine({
        container: agenda,
        view: "list",
        tz,
        agendaDate,
        serverNow,
    });
    mountSectionStates({
        container: agenda,
        view: "list",
        tz,
        agendaDate,
        serverNow,
    });
    const cfg = window.horaAgendaList;
    if (!agendaId || !cfg || cfg.liveEnabled === false || !cfg.liveStateUrl) {
        return;
    }
    const prevDelays = new Map();
    startLiveState({
        agendaId,
        baseUrl: cfg.liveStateUrl,
        pollMs: typeof cfg.livePollMs === "number" ? cfg.livePollMs : 8000,
        jitterMs: typeof cfg.liveJitterMs === "number" ? cfg.liveJitterMs : 4000,
        firstTickMaxMs: typeof cfg.liveFirstTickMaxMs === "number" ? cfg.liveFirstTickMaxMs : 500,
        onUpdate: (payload) => {
            requestAnimationFrame(() => {
                var _a;
                applyListDelays(agenda, payload, prevDelays);
                // After re-stamping effective times, slot the now-line back into the
                // right gap relative to the as-broadcast schedule. v1: only reads
                // data-effective-start-min on each row — does NOT reorder rows.
                // TODO v2: re-sort list rows when delays cross row boundaries.
                repositionListNowLine(agenda, tz, (_a = payload.server_now) !== null && _a !== void 0 ? _a : serverNow);
                applySectionStates({
                    container: agenda,
                    view: "list",
                    tz,
                    agendaDate,
                    serverNow: payload.server_now,
                });
            });
        },
    });
}
/* ---------- Bootstrap ------------------------------------------------- */
function init(container) {
    const state = collect(container);
    container.addEventListener("click", (e) => onClick(state, e));
    container.addEventListener("keydown", (e) => onKeydown(state, e));
    initAddToCalendarTracking(container, "list");
    observeLineWidth(container);
    restoreOuter(state);
    openDrawerFromUrl(state);
    container.querySelectorAll(AGENDA_SELECTOR).forEach(initLiveForAgenda);
}
function boot() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(init);
}
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
}
else {
    boot();
}
